<template>
  <div>
    <div class="row mt-2 no-gutters evollu-form" v-if="showFilters">
      <div class="row">

            <RangeDatePicker
              id="selected-time-date-picker"
              v-model="filters.dateRange"
              class="col-12 col-md-4 datesFilter"
              :placeholder="$t('optometry.leads.date_range')">

              <div slot="label">
                <label class="form-label" style="margin-top: -3px;">
                  {{ $t('statistics.daterange_start') }}
                  <span class="clickable" :class="{'unselectedDateLabel': filters.date != 'created_at', 'selectedDateLabel': filters.date == 'created_at'}" @click="setDate('created_at')">{{ $t('statistics.daterange_created_at') }}</span>
                  <span class="dateSeparatorLabel">/</span>
                  <span class="clickable" :class="{'unselectedDateLabel': filters.date != 'updated_at', 'selectedDateLabel': filters.date == 'updated_at'}" @click="setDate('updated_at')">{{ $t('statistics.daterange_updated_at') }}</span>
                  {{ $t('statistics.daterange_end') }}:
                </label>
              </div>
            </RangeDatePicker>

        <!-- Statuses Filter -->
        <div class="col-12 col-md-4">
          <label class="form-label">{{ $t('leads.table.status') }}:</label>
          <multiselect
            v-model="filters.statuses"
            :options="statuses"
            track-by="id"
            label="name"
            :custom-label="status => $t(status.name)"
            select-label=""
            selected-label=""
            deselect-label="×"
            placeholder="---"
            :searchable="true"
            :allow-empty="true"
            :multiple="true"
            :limit="3"
            :limit-text="(count) => $t('tables.multiselect_limitText_and') + count + this.$t('tables.multiselect_limitText_others')"
          >
            <span slot="noResult">{{$t('patients.noResults')}}</span>
            <template slot="clear" slot-scope="props">
              <div class="multiselect__clear" v-if="filters.statuses.length" @mousedown.prevent.stop="filters.statuses = []"></div>
            </template>
          </multiselect>
        </div>

            <!-- Entities Filter -->
            <div class="col-12 col-md-4">
              <label class="form-label">{{$t('users.inputs.entity')}}:</label>
              <multiselect
                v-model="filters.entities"
                :options="entityOptions"
                track-by="uid"
                :custom-label="formatedEntityLabel"
                label="name"
                select-label=''
                selected-label=''
                deselect-label="×"
                placeholder="---"
                :searchable="true"
                :allow-empty="true"
                :multiple="true"
                :limit="2"
                :limit-text="(count) => $t('tables.multiselect_limitText_and') + count + this.$t('tables.multiselect_limitText_others')"
                :close-on-select="false">
                <span slot="noResult">{{$t('optometry.leads.table.no_results')}}</span>
                <template slot="tag" slot-scope="{ option, remove }">
                <span class="custom__tag">
                  <span>{{ option.name }}&nbsp;<span v-if="option.entities"> 📂</span></span>
                  <i @click="remove(option)" aria-hidden="true" tabindex="1" class="custom__tag-icon"></i>
                </span>
                </template>
                <template slot="clear" slot-scope="props">
                  <div class="multiselect__clear" v-if="filters.entities.length" @mousedown.prevent.stop="filters.entities = []"></div>
                </template>
              </multiselect>
            </div>
      </div>
    </div>

    <v-server-table
      id="anonLeadsTable"
      ref="anonLeadsTable"
      :name="'anonLeadsTable'"
      :columns="columns"
      :options="tableOptions"
      @filter="onFilterUpdate"
      @loaded="onLoaded"
      @loading="onLoading">

      <span slot="status" slot-scope="props">{{ getStatusByProperty('id', props.row.status)  }}</span>

      <span slot="created_at" class="no-break" slot-scope="props">{{ props.row.created_at | localTime }}</span>

      <span slot="updated_at" class="no-break" slot-scope="props">{{ props.row.updated_at | localTime }}</span>

      <span slot="info" slot-scope="props">
        <span :title="$t('references.table.kiosk')" v-if="props.row.kiosk">
                  <i class="fa fa-laptop"></i>
                </span>
                <span :title="$t('references.table.questionnaire')" v-if="props.row.answers">
                  <i class="fa fa-question-circle-o"></i>
                </span>
                <span :title="$t('references.table.hearing_test')" v-if="props.row.ht">
                  <i class="fa fa-headphones"></i>
                </span>
                <span :title="$t('references.table.marketing')" v-if="props.row.marketing">
                  <i class="fa fa-bullhorn"></i>
                </span>
      </span>
      <span slot="has_HearingLoss" slot-scope="props">
        <span :title="$t('leads.table.has_hearing_loss')" v-if="props.row.hasHearingLoss">
          <i class="fa fa-check"></i>
        </span>
      </span>

      <span slot="qualified_at" slot-scope="props">
        <span :title="$t('leads.table.qualified_lead')" v-if="props.row.qualified_at">
          <i class="fa fa-check"></i>
        </span>
      </span>

      <span slot="comment" slot-scope="props">
                <a v-if="hasPermissionsForComments" href="#" @click.prevent="editComment(props.row)">
                  <i v-if="props.row.comment" class="fa fa-comment"></i>
                  <i v-else class="fa fa-comment no-comment"></i>
                </a>
      </span>

    </v-server-table>
  </div>
</template>

<script>
import TableQueryMixin from '@/mixins/TableQueryMixin'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import Multiselect from 'vue-multiselect'
import RangeDatePicker from '@/components/common/RangeDatePicker'
import ClustersAndEntitiesMixin from '@/mixins/ClustersAndEntitiesMixin'
import { mapGetters } from 'vuex'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import { TABLE_ANON_LEADS } from '@/local-storage/LocalStorageKeys'

export default {
  mixins: [
    TableQueryMixin,
    MultiselectLabelFormatter,
    ClustersAndEntitiesMixin
  ],
  components: {
    RangeDatePicker,
    Multiselect
  },
  props: {
    entities: {
      type: Array,
      required: true
    },
    showFilters: {
      type: Boolean,
      required: false,
      default: false
    },
    querySearch: {
      type: String,
      required: false,
      default: null
    },
    customQueries: {
      type: Object,
      required: false,
      default: null
    }
  },
  data: function () {
    return {

      filters: {
        querySearch: '',
        entities: [],
        statuses: [],
        date: 'created_at',
        dateRange: {
          start: null,
          end: null
        }
      },
      queryUrl: `${this.$store.state.evollead_server}/anonleads`,
      columns: ['created_at', 'updated_at', 'name', 'phone', 'status', 'entity', 'info', 'has_HearingLoss', 'qualified_at', 'comment'],
      tableOptions: {
        rowClassCallback: function (row) {
          if (row.selected) {
            return 'highlight'
          }
        },
        filterable: false,
        saveState: true,
        storage: 'local',
        sortable: ['updated_at', 'name', 'email', 'created_at', 'updated_at', 'has_HearingLoss', 'qualified_at'],
        headings: {
          name: this.$t('references.table.name'),
          phone: this.$t('references.table.phone'),
          email: this.$t('references.table.email'),
          created_at: this.$t('references.table.created_at'),
          status: this.$t('references.table.status'),
          entity: this.$t('leads.table.entity'),
          info: this.$t('references.table.info'),
          updated_at: this.$t('leads.table.updated_at'),
          has_HearingLoss: this.$t('leads.table.hl'),
          qualified_at: this.$t('leads.table.eql'),
          comment: this.$t('leads.details.comment')
        },
        customFilters: ['status', 'entity', 'datestart', 'dateend', 'date'],
        columnsDisplay: {
          name: 'min_mobile',
          phone: 'desktop',
          email: 'desktop',
          status: 'min_tablet',
          entity: 'min_tablet',
          info: 'min_tablet',
          created_at: 'desktop',
          updated_at: 'min_mobile',
          has_HearingLoss: 'desktop',
          qualified_at: 'min_mobile'
        },
        columnsClasses: {
          name: 'name',
          phone: 'phone',
          email: 'email',
          status: 'status',
          entity: 'entity',
          info: 'text-center',
          created_at: 'created_at',
          updated_at: 'updated_at',
          has_HearingLoss: 'text-center',
          qualified_at: 'text-center',
          comment: 'text-center'
        },
      },
    }
  },
  mounted: function () {
    this.checkActiveFilters()
  },
  created () {
    this.loadLocalStorage()
  },
  methods: {
    getStatusByProperty: function (property, value) {
      let status = this.statuses.find(status => status[property] === value)
      return status ? this.$t(status.name) : null
    },
    onLoading: function () {
      this.$emit('loading')
    },
    onLoaded: function (state) {
      this.$emit('loaded', state)
    },
    setDate: function (date) {
      this.filters.date = date
    },
    editComment: function (lead) {
      this.$emit('editLead', lead)
    },
    checkActiveFilters: function () {
      return new Promise((resolve) => {
        this.$emit('filters-updated', this.activeFilters)
        if (this.activeFilters > 0) {
          this.$emit('update:showFilters', true)
        }
        resolve()
      })
    },
    reload: function () {
      this.$refs.anonLeadsTable.refresh()
    },
    loadLocalStorage: function () {
      let state = LocalStorageHelper.get(TABLE_ANON_LEADS)
      if (state) {
        if (Array.isArray(state.selectedEntityUids) && state.selectedEntityUids && state.selectedEntityUids.length > 0) {
          this.filters.entities = this.reloadSelectedClustersAndEntities(this.entityOptions, state.selectedEntityUids)
        }
      }
    },
  },
  computed: {
    ...mapGetters({
      clusters: 'clusters',
      statuses: 'leads/statuses'
    }),
    activeFilters: function () {
      let count = 0
      if (this.filters.entities.length > 0) count++
      if (this.filters.statuses.length > 0) count++
      if (this.filters.dateRange.start || this.filters.dateRange.end) count++
      return count
    },
    entityOptions: function () {
      return this.createClustersAndEntitiesList(this.clusters, this.entities)
    },
    leadsService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.evollead_server) || null
    },
    hasPermissionsForComments: function () {
      return this.leadsService.user_permissions.filter(permission => permission.access_type >= 2 && permission.info_type === 1500)
    }
  },
  watch: {
    querySearch: {
      handler: function (value) {
        this.filters.querySearch = value || ''
      },
      immediate: true
    },
    customQueries: {
      handler: function (value) {
        if (value.status !== '') {
          let statuses = value.status.split(',').map(status => parseInt(status))
          this.filters.statuses = statuses.map(status => this.statuses.find(_status => _status.id === status)).filter(status => status)
        }

        if (value.date && value.date !== '') {
          this.filters.date = value.date
        }
        if (value.datestart && value.datestart !== '') {
          this.filters.dateRange.start = value.datestart
        }
        if (value.dateend && value.dateend !== '') {
          this.filters.dateRange.end = value.dateend
        }
      },
      immediate: true
    },
    'filters.entities': function (options) {
      const selectedOptions = this.getSelectedOptionsIdsAndUids(options)
      this.emitTableFilter('vue-tables.anonLeadsTable.filter::entity', Array.from(selectedOptions.ids).join(','))
      this.$nextTick(() => {
        let state = LocalStorageHelper.get(TABLE_ANON_LEADS)
        state.selectedEntityUids = Array.from(selectedOptions.uids)
        LocalStorageHelper.set(TABLE_ANON_LEADS, state)
      })
    },
    'filters.statuses': function (statuses) {
      this.emitTableFilter('vue-tables.anonLeadsTable.filter::status', statuses.map(status => status.id).join(','))
    },
    'filters.querySearch': function (value) {
      // this.$refs.anonLeadsTable.setFilter(value)
    },
    'filters.dateRange': function (value) {
      this.$refs.anonLeadsTable.customQueries.datestart = value.start || ''
      this.emitTableFilter('vue-tables.anonLeadsTable.filter::dateend', value.end || '')
    },
    'filters.date': function (date) {
      if (date === '') {
        this.$refs.anonLeadsTable.customQueries.date = date
      } else {
        this.emitTableFilter('vue-tables.anonLeadsTable.filter::date', date)
      }
    },
    activeFilters: function (nrFilters) {
      this.$emit('filters-updated', nrFilters)
    }
  }
}

</script>
<style lang="scss">
.unselectedDateLabel {
  font-size: 0.6rem !important;
  color: #ada7a1 !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  text-decoration: line-through !important;
}

.dateSeparatorLabel {
  font-size: 0.6rem !important;
  color: #ada7a1 !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
}

.selectedDateLabel {
  font-size: 0.6rem !important;
  color: #29c4a9 !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
}
</style>
